import { useNavBarHeight } from '@/components/AppTabBar/height'
import { Glyph, type GlyphName } from '@/components/svgs/Glyph'
import Logo from '@/components/svgs/Logo'
import SelectionIndicator from '@/components/svgs/SelectionIndicator'
import { colors } from '@/constants/colors'
import { Pressable } from '@/ui/core/pressable'
import { useIsDesktop } from '@/ui/hooks/useIsDesktop'
import { cn } from '@/ui/utils'
import type { BottomTabBarProps } from '@react-navigation/bottom-tabs'
import { BlurView } from 'expo-blur'
import { useEffect } from 'react'
import { type LayoutChangeEvent, StyleSheet, Text, View } from 'react-native'
import Animated, {
	useAnimatedStyle,
	useReducedMotion,
	useSharedValue,
	withSpring,
	withTiming,
} from 'react-native-reanimated'

const GLYPHS: Record<string, GlyphName> = {
	index: 'house',
	care: 'plus',
	billing: 'dollar',
	chat: 'chat',
	profile: 'person',
}

const DISC = 56
const RING = 66

/**
 * One destination as a disc: light pink with an ink glyph at rest, white when chosen, with a
 * thin ink ring that settles around the chosen one. The disc breathes up on
 * selection and back, one spring, no bounce.
 */
const Disc = ({
	glyph,
	text,
	focused,
	badge,
}: {
	glyph?: GlyphName
	text?: string
	focused: boolean
	badge?: string | number
}) => {
	const scale = useSharedValue(1)
	const ring = useSharedValue(focused ? 1 : 0)
	const reduceMotion = useReducedMotion()
	useEffect(() => {
		scale.value = reduceMotion
			? focused
				? 1.06
				: 1
			: withSpring(focused ? 1.06 : 1, { damping: 14, stiffness: 220 })
		ring.value = withTiming(focused ? 1 : 0, { duration: reduceMotion ? 0 : 220 })
	}, [focused, scale, ring, reduceMotion])
	const discStyle = useAnimatedStyle(() => ({ transform: [{ scale: scale.value }] }))
	const ringStyle = useAnimatedStyle(() => ({
		opacity: ring.value,
		transform: [{ scale: 0.9 + ring.value * 0.1 }],
	}))

	return (
		<View className="items-center justify-center" style={{ width: RING, height: RING }}>
			<Animated.View
				pointerEvents="none"
				className="absolute rounded-full border-2 border-ink"
				style={[{ width: RING, height: RING }, ringStyle]}
			/>
			<Animated.View
				className={cn(
					'items-center justify-center rounded-full',
					focused ? 'bg-white' : 'bg-blush'
				)}
				style={[{ width: DISC, height: DISC }, discStyle]}
			>
				{glyph ? (
					<Glyph name={glyph} size={24} color={colors.ink} />
				) : (
					<Text
						className="font-bold text-[21px] tracking-[0.06em] text-ink"
						style={{ paddingLeft: 1.5, paddingTop: 1 }}
					>
						{text}
					</Text>
				)}
			</Animated.View>
			{badge !== undefined && badge !== 0 && (
				<View className="absolute right-0.5 top-0.5 h-[18px] w-[18px] rounded-full border-2 border-ground bg-danger" />
			)}
		</View>
	)
}

/**
 * The navigation, floating as glass over the top of every tab: the wordmark, then the five
 * destinations as discs, each with a word beneath. On a wide
 * screen it is the header bar with the tabs as words on the right.
 */
export const AppTabBar = ({
	state,
	descriptors,
	navigation,
	insets,
}: BottomTabBarProps) => {
	const isDesktop = useIsDesktop()
	const setHeight = useNavBarHeight((s) => s.setHeight)

	const items = state.routes.map((route, index) => {
		const options = descriptors[route.key]?.options
		const title = options?.title
		const badge = options?.tabBarBadge
		const label =
			typeof title === 'string' ? title : route.name === 'index' ? 'Home' : route.name
		const isFocused = state.index === index

		const onPress = () => {
			const event = navigation.emit({
				type: 'tabPress',
				target: route.key,
				canPreventDefault: true,
			})
			if (!isFocused && !event.defaultPrevented) {
				navigation.navigate(route.name, route.params)
			}
		}

		return { key: route.key, name: route.name, label, badge, isFocused, onPress }
	})

	if (isDesktop) {
		return (
			<View className="h-[88px] w-full flex-row items-center justify-between px-14 bg-ground">
				<Logo className="h-10 w-[120px]" />
				<View className="flex-row items-center" style={{ gap: 36 }}>
					{items.map((item) => (
						<Pressable
							key={item.key}
							onPress={item.onPress}
							accessibilityRole="tab"
							accessibilityState={{ selected: item.isFocused }}
							className="items-center"
						>
							<Text className="font-display text-h6 uppercase text-ink">
								{item.label}
							</Text>
							{item.isFocused && <SelectionIndicator width="100%" />}
						</Pressable>
					))}
				</View>
			</View>
		)
	}

	return (
		<View
			className="absolute top-0 right-0 left-0 z-10"
			style={{ paddingTop: insets.top }}
			onLayout={(e: LayoutChangeEvent) => setHeight(e.nativeEvent.layout.height)}
		>
			{/* Glass: the scene shows through a blur with a wash of the ground over it. */}
			<BlurView intensity={28} tint="light" style={StyleSheet.absoluteFill} />
			<View
				style={[StyleSheet.absoluteFill, { backgroundColor: 'rgba(254,202,201,0.8)' }]}
			/>
			<View className="h-14 items-center justify-center">
				<Logo className="h-7 w-[84px]" />
			</View>
			<View className="flex-row justify-between px-3 pt-1 pb-4">
				{items.map((item) => (
					<Pressable
						key={item.key}
						onPress={item.onPress}
						accessibilityRole="tab"
						accessibilityState={{ selected: item.isFocused }}
						accessibilityLabel={
							item.badge ? `${item.label}, ${item.badge} unread` : item.label
						}
						className="items-center"
						// Four rather than six between the disc and its label. A disc is a round mark
						// with air built into its own edge, so the measured gap under it reads
						// larger than the same number under a straight-sided thing.
						style={{ width: 70, gap: 4 }}
					>
						<Disc
							glyph={GLYPHS[item.name] ?? 'sparkle'}
							focused={item.isFocused}
							badge={item.badge}
						/>
						<Text
							numberOfLines={1}
							maxFontSizeMultiplier={1.3}
							className={cn(
								'font-bold text-[11px] uppercase tracking-[0.08em]',
								item.isFocused ? 'text-ink' : 'text-softInk'
							)}
						>
							{item.label}
						</Text>
					</Pressable>
				))}
			</View>
		</View>
	)
}
